import { createBrowserRouter, Navigate, redirect } from 'react-router';
import LoginRoute from '@/routes/LoginRoute';
import RegistrationRoute from '@/routes/RegistrationRoute';
import ForgotPasswordRoute from '@/routes/ForgotPasswordRoute';
import ProjectsRoute from '@/routes/ProjectsRoute';
import ProjectRoute from '@/routes/ProjectRoute';
import AnalystRoute from '@/routes/analyst/AnalystRoute';
import { createProject, deleteProject, getProject, getProjects, updateProject } from '@/api/projects';

import ErrorBoundary from '@/components/core/ErrorBoundry';

import EditProjectModal from './components/projects/EditProjectModal';
import { getRegistry } from './api/registry';
import { registryActions } from './stores/registryStore';
import buildRouter from './routes/build';
import analysesRouter from './routes/analyses';
import { getPathwayParamInfo, getPathways } from './api/pathways';
import { getPathwayTemplates, getPathwayTemplate } from './api/pathwayTemplates';
import { getSystems } from './api/systems';
import comparisonsRouter from './routes/comparisons';
import userRouter from './routes/user';
import RootRouter from './routes/RootRouter';
import { getComparisons } from './api/comparisons';
import { resourcesActions } from './stores/resourcesStore';
import dashboardRouter from './routes/dashboard';
import { keyBy } from './utils/miscUtils';
import useAnalystStore, { analystActions } from './stores/analystStore';
import { chatHistoryActions } from './stores/chatHistoryStore';
import useSettingsStore, { settingsActions } from './stores/settingsStore';
import { authActions } from './stores/authStore';
import PathwayNavigatorListRoute from './routes/pathwayNavigator/PathwayNavigatorListRoute';
import PathwayNavigatorRoute from './routes/pathwayNavigator/PathwayNavigatorRoute';
import PathwayNavigatorLayout from './routes/pathwayNavigator/PathwayNavigatorLayout';
import DataviewerRoute from './routes/dataviewer/DataviewerRoute';
import { dataviewerActions } from './stores/dataviewerStore';
import GconRoute from './routes/gcon/GconRoute';
import GconSessionRoute from './routes/gcon/GconSessionRoute';
import { getGconSession, listGconSessions } from './api/gcon';
import { pathwayTemplateAnalysisActions } from './stores/pathwayTemplateAnalysisStore';
import { pathwayTemplatesActions } from './stores/pathwayTemplatesStore';
import featureConfig from './featureConfig';
import { isMITX } from './utils/stackUtils';

const moocTitle = 'Systems Thinking in Energy: Navigating Costs, Emissions, and Impact';

const routes = [
  {
    path: '/login',
    element: <LoginRoute />,
    shouldRevalidate: () => false,
    loader: () => {
      settingsActions.fetchSettings();
    },
  },
  {
    path: '/forgot-password',
    element: <ForgotPasswordRoute />,
    shouldRevalidate: () => false,
  },
];

if (featureConfig.enableRegistration) {
  routes.push({
    path: '/register',
    element: <RegistrationRoute />,
    shouldRevalidate: () => false,
    loader: () => {
      settingsActions.fetchSettings();
    },
  });
}

const router = createBrowserRouter([
  ...routes,
  {
    path: '/',
    errorElement: <ErrorBoundary />,
    element: <RootRouter />,
    id: 'root',
    shouldRevalidate: () => false,
    loader: async () => {
      const authStatus = await authActions.isAuthenticated();

      if (!authStatus) {
        return redirect('/login');
      }

      settingsActions.fetchSettings();

      const {
        data: { params },
      } = await getPathwayParamInfo();
      return { pathwayParams: keyBy(params, 'name') };
    },
    children: [
      userRouter,
      {
        path: '/',
        id: 'projects',
        element: <ProjectsRoute />,
        shouldRevalidate: options => {
          const { formMethod } = options;
          if (formMethod !== 'GET') {
            return true;
          }

          return false;
        },
        loader: async () => {
          const [projects, pathwayTemplates] = await Promise.all([getProjects(), getPathwayTemplates()]);
          const projectResults = projects?.data?.results ?? [];

          // If manageProjects is disabled and there are projects, redirect to the first one
          if (!featureConfig.manageProjects && projectResults.length > 0) {
            return redirect(`/projects/${projectResults[0].id}`);
          }

          return {
            projects: projectResults,
            nextPage: projects?.data?.next ?? null,
            pathwayTemplates: pathwayTemplates?.data?.results ?? [],
          };
        },
        handle: {
          title: _ => {
            return {
              title: 'Welcome to Sesame',
            };
          },
        },
        children: [
          {
            path: '/projects/new',
            element: <EditProjectModal title="New Project" />,
            action: async ({ request }) => {
              const form = await request.formData();
              const project = Object.fromEntries(form);

              const { data } = await createProject(project);
              return redirect(`/projects/${data.id}`);
            },
          },
          {
            path: '/projects/:projectId/edit',
            element: <EditProjectModal title="Edit Project" />,
            loader: async ({ params: { projectId } }) => {
              const { data: project } = await getProject(projectId);
              return project;
            },
            action: async ({ request, params }) => {
              const { projectId } = params;
              const form = await request.formData();
              const project = Object.fromEntries(form);

              try {
                await updateProject(projectId, project);
              } catch (err) {
                console.error(err);
              }

              return redirect('/');
            },
          },
          {
            path: '/projects/:projectId/destroy',
            action: async ({ params }) => {
              const { projectId } = params;
              try {
                await deleteProject(projectId);
              } catch (err) {
                console.error(err);
              }

              return redirect('/');
            },
          },
        ],
      },
      {
        path: '/projects/:projectId',
        id: 'project',
        element: <ProjectRoute />,
        shouldRevalidate: options => {
          const { currentParams, nextParams, formMethod, nextUrl, currentUrl } = options;

          if (nextUrl.pathname.includes('edit-title') || currentUrl.pathname.includes('edit-title')) return false;

          // Revalidate if project id changes
          if (currentParams.projectId !== nextParams.projectId) return true;

          // Revalidate after any form submission
          if (formMethod && formMethod !== 'GET') return true;

          return false;
        },
        loader: async ({ params }) => {
          const { projectId } = params;
          const [project, pathways, systems, comparisons, registry] = await Promise.all([
            getProject(projectId),
            getPathways(projectId),
            getSystems(projectId),
            getComparisons(projectId),
            getRegistry(projectId),
          ]);

          resourcesActions.setResources({
            project: project?.data,
            pathways: pathways?.data,
            systems: systems?.data,
            comparisons: comparisons?.data,
          });

          registryActions.init(projectId, registry?.data?.node_types);

          return {
            project: project.data,
            pathways: pathways.data,
            systems: systems.data,
            comparisons: comparisons.data,
            registry: registry.data,
          };
        },
        handle: {
          crumb: data => {
            if (isMITX()) {
              return moocTitle;
            }
            return data?.project?.name;
          },
          title: data => {
            if (isMITX()) {
              return { title: `Dashboard: ${moocTitle}` };
            }
            return { title: `Dashboard: ${data.project.name}` };
          },
        },
        children: [
          {
            index: true,
            element: <Navigate to="dashboard" replace />,
          },
          dashboardRouter,
          buildRouter,
          analysesRouter,
          comparisonsRouter,
        ],
      },
      {
        path: '/analyst',
        id: 'analyst',
        loader: async () => {
          await settingsActions.fetchSettings();

          if (!useSettingsStore.getState()?.features?.agent_analyst) {
            return redirect('/');
          }

          // Land on the empty view. Previous conversations remain available
          // from the server-backed recent list. Skip if a stream is in flight.
          const analyst = useAnalystStore.getState();
          if ((analyst.messages ?? []).length > 0 && !analyst.isStreaming) {
            analystActions.clearConversation();
          }

          if ((useAnalystStore.getState().messages ?? []).length === 0) {
            await chatHistoryActions.fetch();
          }

          return null;
        },
        element: <AnalystRoute />,
        handle: {
          title: _ => ({ title: 'Sesame Analyst' }),
        },
      },
      {
        path: '/gcon',
        loader: async () => {
          await settingsActions.fetchSettings();
          if (!useSettingsStore.getState()?.features?.gcon) return redirect('/');
          const { data } = await listGconSessions();
          return { sessions: data.results };
        },
        element: <GconRoute />,
        handle: {
          title: _ => ({ title: 'GCON Hot Spot Analysis' }),
        },
      },
      {
        path: '/gcon/:sessionId',
        loader: async ({ params }) => {
          await settingsActions.fetchSettings();
          if (!useSettingsStore.getState()?.features?.gcon) return redirect('/');
          try {
            const { data } = await getGconSession(params.sessionId);
            return { session: data };
          } catch (err) {
            if (err?.response?.status === 404) return redirect('/gcon');
            throw err;
          }
        },
        element: <GconSessionRoute />,
        handle: {
          title: _ => ({ title: 'GCON Hot Spot Analysis' }),
        },
      },
      {
        path: '/dataviewer',
        element: <DataviewerRoute />,
        loader: async ({ request }) => {
          const searchParams = new URL(request.url).searchParams;
          const category = searchParams.get('category');
          const module = searchParams.get('module');

          dataviewerActions.reset();
          await Promise.all([
            dataviewerActions.loadDatasets(),
            dataviewerActions.selectDataset(
              category && module ? category : 'powerplant',
              category && module ? module : 'vre-wind-plant',
            ),
          ]);

          return null;
        },
        handle: {
          title: _ => ({ title: 'Data Viewer' }),
        },
      },
      {
        path: '/pathway-navigator',
        id: 'pathway-navigator',
        element: <PathwayNavigatorLayout />,
        children: [
          {
            index: true,
            element: <PathwayNavigatorListRoute />,
            loader: async () => {
              const [pathwayTemplates, projects] = await Promise.all([getPathwayTemplates(), getProjects()]);

              pathwayTemplatesActions.setPathwayTemplates(pathwayTemplates.data);

              return {
                projects: projects?.data?.results ?? [],
                pathwayTemplates: pathwayTemplates?.data?.results ?? [],
              };
            },
            handle: {
              title: _ => {
                return {
                  title: 'Pathway Navigator',
                };
              },
            },
          },
          {
            path: ':templateId',
            element: <PathwayNavigatorRoute />,
            shouldRevalidate: ({ currentParams, nextParams }) => {
              if (currentParams.templateId !== nextParams.templateId) return true;
              return false;
            },
            loader: async ({ params: { templateId } }) => {
              const pathwayTemplate = await getPathwayTemplate(templateId);

              pathwayTemplateAnalysisActions.clear();
              pathwayTemplateAnalysisActions.fetchLca(templateId);
              pathwayTemplateAnalysisActions.fetchTea(templateId);
              pathwayTemplateAnalysisActions.fetchLcaBreakdown(templateId);
              pathwayTemplateAnalysisActions.fetchLcaAggregated(templateId);
              pathwayTemplateAnalysisActions.fetchTeaAggregated(templateId);

              return {
                pathwayTemplate: pathwayTemplate.data,
              };
            },
            handle: {
              title: loaderData => {
                return {
                  title: `Analyze Pathway: ${loaderData?.pathwayTemplate?.name}`,
                };
              },
            },
          },
        ],
      },
    ],
  },
]);

export default router;
